import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, IconButton, Tooltip, TextField, InputAdornment, FormControl, Select, MenuItem, Checkbox, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column, RowAction, FilterChip } from './DataTable';
import TransactionDetailsDrawer from './TransactionDetailsDrawer';
import RTGSActionDialog from './RTGSActionDialog';
import AmendTransactionDialog from './AmendTransactionDialog';
import CancelTransactionDialog from './CancelTransactionDialog';
import { useToast } from './ui/ToastProvider';
import { VALID_MTOS, VALID_SUB_AGENTS } from '../utils/constants';
import { transactionStore } from '../utils/transactionStore';
import { DEMO_TRANSACTIONS } from '../utils/demoTransactions';
type PayoutType = 'FT' | 'IBFT' | 'OTC';
type LifecycleStatus = 'FAILED';
type CurrentState = 'TITLE_FETCH' | 'BALANCE_CHECK' | 'FT_POST' | 'RAAST_POST' | 'ONELINK_POST' | 'RTGS_POST';
interface FailedTransaction {
  id: string;
  mtoName: string;
  mtoRef: string;
  bankRef: string;
  branchCode: string;
  agentCode: string;
  payoutType: PayoutType;
  amountFCY: string;
  currencyFCY: string;
  amountPKR: string;
  corridor: string;
  purposeCode: string;
  purposeText: string;
  remitterName: string;
  beneficiaryName: string;
  beneficiaryIBAN: string;
  currentState: CurrentState;
  lifecycleStatus: LifecycleStatus;
  createdAt: string;
  updatedAt: string;
  failedAt: string;
  attemptCount: number;
  canMoveToRTGS: boolean;
  railRefs: {
    RRN?: string;
    STAN?: string;
    RaastMsgId?: string;
    UETR?: string;
  };
  stateHistory: any[];
}
// Mock failed transactions
export const MOCK_FAILED_TRANSACTIONS: FailedTransaction[] = Array(30).fill(null).map((_, index) => {
  const payoutTypes: PayoutType[] = ['FT', 'IBFT', 'OTC'];
  const payoutType = payoutTypes[index % 3];
  const failStates: CurrentState[] = payoutType === 'FT' ? ['TITLE_FETCH', 'BALANCE_CHECK', 'FT_POST'] : ['TITLE_FETCH', 'RAAST_POST', 'ONELINK_POST'];
  const currentState = failStates[index % failStates.length];
  const canMoveToRTGS = ['TITLE_FETCH', 'BALANCE_CHECK', 'FT_POST'].includes(currentState);
  return {
    id: `TXN-${50000 + index}`,
    mtoName: index % 10 === 0 ? '' : VALID_MTOS[index % VALID_MTOS.length],
    mtoRef: `MTO-${60000 + index}`,
    bankRef: `BNK-${70000 + index}`,
    branchCode: payoutType === 'OTC' ? ['0001', '0002', '0003'][index % 3] : '',
    agentCode: payoutType === 'OTC' ? VALID_SUB_AGENTS[index % VALID_SUB_AGENTS.length] : '',
    payoutType,
    amountFCY: (1000 + index * 50).toFixed(2),
    currencyFCY: 'USD',
    amountPKR: ((1000 + index * 50) * 280).toLocaleString(),
    corridor: ['PK-AE', 'PK-SA', 'PK-UK', 'PK-US'][index % 4],
    purposeCode: ['FAM', 'SAL', 'EDU', 'MED'][index % 4],
    purposeText: ['Family Support', 'Salary', 'Education', 'Medical'][index % 4],
    remitterName: `Remitter ${index + 1}`,
    beneficiaryName: `Beneficiary ${index + 1}`,
    beneficiaryIBAN: `PK${String(index).padStart(22, '0')}`,
    currentState,
    lifecycleStatus: 'FAILED',
    createdAt: new Date(Date.now() - (index + 1) * 3600000).toISOString(),
    updatedAt: new Date(Date.now() - index * 3600000).toISOString(),
    failedAt: new Date(Date.now() - index * 3600000).toISOString(),
    attemptCount: Math.floor(index / 10) + 1,
    canMoveToRTGS,
    railRefs: {},
    stateHistory: [{
      stateCode: currentState,
      status: 'FAIL',
      timestamp: new Date(Date.now() - index * 3600000).toISOString(),
      primaryRef: `${currentState}-${index}`,
      attempts: Math.floor(index / 10) + 1
    }]
  };
});
const FailedTransactionsTable: React.FC = () => {
  const [transactions, setTransactions] = useState<FailedTransaction[]>(MOCK_FAILED_TRANSACTIONS);
  const [filteredTransactions, setFilteredTransactions] = useState<FailedTransaction[]>(MOCK_FAILED_TRANSACTIONS);
  const [selectedTransaction, setSelectedTransaction] = useState<FailedTransaction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rtgsDialogOpen, setRtgsDialogOpen] = useState(false);
  const [filterChips, setFilterChips] = useState<FilterChip[]>([]);
  const [filters, setFilters] = useState({
    search: '',
    payoutType: [] as PayoutType[],
    currentState: [] as CurrentState[],
    mtoRef: '',
    txnId: ''
  });
  const {
    showToast
  } = useToast();
  const [amendDialogOpen, setAmendDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAmendTransaction, setSelectedAmendTransaction] = useState<FailedTransaction | null>(null);
  const [selectedCancelTransaction, setSelectedCancelTransaction] = useState<FailedTransaction | null>(null);
  const [storeUpdateCount, setStoreUpdateCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkCancelDialogOpen, setBulkCancelDialogOpen] = useState(false);
  // Add demo failed transactions
  const demoFailedTransactions = DEMO_TRANSACTIONS.filter((t: any) => t.lifecycleStatus === 'FAILED' || t.lifecycleStatus === 'IN_PROGRESS') as any[];
  const allFailedTransactions = [...MOCK_FAILED_TRANSACTIONS, ...demoFailedTransactions];
  // Subscribe to store changes to remove approved amendments from failed list
  useEffect(() => {
    const handleStoreUpdate = () => {
      setStoreUpdateCount(prev => prev + 1);
    };
    const unsubscribe = transactionStore.subscribe(handleStoreUpdate);
    return unsubscribe;
  }, []);
  // Initialize and update transactions based on store overrides
  useEffect(() => {
    // Apply store overrides to filter out approved amendments (they move to IN_PROGRESS)
    const updatedTransactions = allFailedTransactions.map((txn: any) => {
      const override = transactionStore.getTransactionOverride(txn.id);
      if (override) {
        return {
          ...txn,
          amendStatus: override.amendStatus,
          ...(override.lifecycleStatus && {
            lifecycleStatus: override.lifecycleStatus
          }),
          ...(override.beneficiaryIBAN && {
            beneficiaryIBAN: override.beneficiaryIBAN
          }),
          ...(override.beneficiaryName && {
            beneficiaryName: override.beneficiaryName
          }),
          ...(override.updatedAt && {
            updatedAt: override.updatedAt
          })
        };
      }
      // Check for pending amendment status
      const amendStatus = transactionStore.getAmendmentStatusForTransaction(txn.id);
      if (amendStatus !== 'NONE') {
        return {
          ...txn,
          amendStatus
        };
      }
      return txn;
    })
    // Filter out transactions that have been moved to RTGS
    .filter((txn: any) => {
      const rtgsOverride = transactionStore.getRTGSOverride(txn.id);
      if (rtgsOverride) {
        return false; // Remove from failed list - it's been moved to RTGS
      }
      return true;
    })
    // Filter out transactions whose amendments were approved (they're no longer FAILED)
    .filter((txn: any) => {
      const override = transactionStore.getTransactionOverride(txn.id);
      if (override && override.amendStatus === 'APPROVED' && override.lifecycleStatus === 'IN_PROGRESS') {
        return false; // Remove from failed list - it's been reprocessed
      }
      return true;
    })
    // Filter out repushed transactions
    .filter((txn: any) => {
      const repushed = transactionStore.getRepushedTransactions();
      return !repushed.some(r => r.id === txn.id);
    });
    setTransactions(updatedTransactions);
  }, [storeUpdateCount]);
  useEffect(() => {
    let filtered = transactions;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(txn => txn.id.toLowerCase().includes(searchLower) || txn.mtoName.toLowerCase().includes(searchLower) || txn.mtoRef.toLowerCase().includes(searchLower) || txn.bankRef.toLowerCase().includes(searchLower));
    }
    if (filters.txnId) {
      filtered = filtered.filter(txn => txn.id.toLowerCase().includes(filters.txnId.toLowerCase()));
    }
    if (filters.payoutType.length > 0) {
      filtered = filtered.filter(txn => filters.payoutType.includes(txn.payoutType));
    }
    if (filters.currentState.length > 0) {
      filtered = filtered.filter(txn => filters.currentState.includes(txn.currentState));
    }
    setFilteredTransactions(filtered);
  }, [filters, transactions]);
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(new Set(filteredTransactions.map(t => t.id)));
    } else {
      setSelectedIds(new Set());
    }
  };
  const handleSelectOne = (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
    const newSelected = new Set(selectedIds);
    if (event.target.checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };
  const handleBulkCancelClick = () => {
    if (selectedIds.size === 0) return;
    setBulkCancelDialogOpen(true);
  };
  const handleBulkCancelConfirm = async (reason: string) => {
    const count = selectedIds.size;
    setTransactions(prev => prev.filter(t => !selectedIds.has(t.id)));
    setSelectedIds(new Set());
    showToast(`Cancellation request submitted for ${count} transactions`, 'success');
  };
  const handleAmendClick = (transaction: FailedTransaction) => {
    setSelectedAmendTransaction(transaction);
    setAmendDialogOpen(true);
  };
  const handleAmendConfirm = async (reason: string, newAccount?: string, fetchedTitle?: string) => {
    if (!selectedAmendTransaction) return;
    // Submit amendment to the shared transaction store
    transactionStore.submitAmendment(selectedAmendTransaction, newAccount || '', fetchedTitle || '', reason);
    // Update the transaction's amendStatus in the local list
    setTransactions(prev => prev.map(t => t.id === selectedAmendTransaction.id ? {
      ...t,
      amendStatus: 'PENDING'
    } as any : t));
    showToast(`Amendment request submitted for ${selectedAmendTransaction.id}. Sent to checker for approval.`, 'success');
    setAmendDialogOpen(false);
    setSelectedAmendTransaction(null);
  };
  const handleCancelClick = (transaction: FailedTransaction) => {
    setSelectedCancelTransaction(transaction);
    setCancelDialogOpen(true);
  };
  const handleCancelConfirm = async (reason: string) => {
    if (!selectedCancelTransaction) return;
    showToast(`Cancellation request submitted for ${selectedCancelTransaction.id}`, 'success');
    setCancelDialogOpen(false);
    setSelectedCancelTransaction(null);
  };
  const handleRepush = (transaction: FailedTransaction) => {
    // Add to repushed transactions store
    transactionStore.repushTransaction(transaction);
    // Remove from failed transactions list
    setTransactions(prev => prev.filter(t => t.id !== transaction.id));
    showToast(`Transaction ${transaction.id} repushed and will resume from ${transaction.currentState.replace(/_/g, ' ')}`, 'success');
  };
  const handleMoveToRTGS = (transaction: FailedTransaction) => {
    setSelectedTransaction(transaction);
    setRtgsDialogOpen(true);
  };
  const handleRTGSConfirm = (notes?: string) => {
    if (!selectedTransaction) return;
    // Add to RTGS store - pass notes so they're tracked
    transactionStore.addToRTGS(selectedTransaction, notes);
    // Remove from failed transactions list
    setTransactions(prev => prev.filter(t => t.id !== selectedTransaction.id));
    showToast(`Transaction ${selectedTransaction.id} moved to RTGS`, 'success');
    setRtgsDialogOpen(false);
    setSelectedTransaction(null);
  };
  const rowActions: RowAction<FailedTransaction>[] = [{
    icon: 'lucide:eye',
    label: 'View Details',
    onClick: row => {
      setSelectedTransaction(row);
      setDrawerOpen(true);
    }
  }, {
    icon: 'lucide:edit',
    label: 'Amend',
    onClick: row => handleAmendClick(row),
    condition: (row: any) => (row.payoutType === 'FT' || row.payoutType === 'IBFT') && row.currentState === 'TITLE_FETCH' && row.lifecycleStatus === 'FAILED' && (!row.amendStatus || row.amendStatus === 'NONE' || row.amendStatus === 'REJECTED')
  }, {
    icon: 'lucide:x-circle',
    label: 'Cancel',
    onClick: row => handleCancelClick(row)
  }, {
    icon: 'lucide:rotate-cw',
    label: 'Repush',
    onClick: row => handleRepush(row)
  }, {
    icon: 'lucide:arrow-right-circle',
    label: 'Move to RTGS',
    onClick: row => handleMoveToRTGS(row),
    condition: row => row.canMoveToRTGS
  }];
  const columns: Column<FailedTransaction>[] = [{
    id: 'selection',
    label: '',
    renderCell: row => <Checkbox checked={selectedIds.has(row.id)} onChange={e => handleSelectOne(e, row.id)} size="small" onClick={e => e.stopPropagation()} data-id="element-1162" data-mp="izmbc4olksc2" />
  }, {
    id: 'id',
    label: 'Txn ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1163" data-mp="16oythu1wkaqi">
          {row.id}
        </Typography>,
    renderFilter: () => <TextField size="small" placeholder="Search" value={filters.txnId} onChange={e => setFilters({
      ...filters,
      txnId: e.target.value
    })} sx={{
      width: '100%',
      minWidth: 120
    }} InputProps={{
      startAdornment: <InputAdornment position="start" data-id="element-1165" data-mp="156oz87dqo7bs">
                <Icon icon="lucide:search" width={14} height={14} data-id="element-1166" data-mp="1a3ynvktb0eyk" />
              </InputAdornment>,
      sx: {
        height: 36,
        fontSize: '0.8rem'
      }
    }} data-id="element-1164" data-mp="mgh2c70mud41" />
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Box sx={{
      minWidth: 150
    }} data-id="element-1167" data-mp="1o2okkzc7ocme">
          {row.mtoName ? <Typography variant="body2" fontWeight={500} noWrap data-id="element-1168" data-mp="1svrgvf8di06a">
              {row.mtoName}
            </Typography> : <Chip label="No MTO" size="small" sx={{
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        color: '#EF4444',
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-1169" data-mp="3di93p1iq1wq6" />}
        </Box>,
    renderFilter: () => <FormControl size="small" fullWidth sx={{
      minWidth: 120
    }} data-id="element-1170" data-mp="2umsneposwlc6">
          <Select value={filters.mtoRef || 'all'} onChange={e => setFilters({
        ...filters,
        mtoRef: e.target.value === 'all' ? '' : e.target.value
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} data-id="element-1171" data-mp="2pohhgq1mmuz8">
            <MenuItem value="all" data-id="element-1172" data-mp="eu3hcvcc0gea">All</MenuItem>
            {VALID_MTOS.map(name => <MenuItem key={name} value={name} data-id="element-1173" data-mp="lx087s2ssheu">
                {name}
              </MenuItem>)}
          </Select>
        </FormControl>
  }, {
    id: 'payoutType',
    label: 'Type',
    renderCell: row => <Chip label={row.payoutType} size="small" sx={{
      backgroundColor: row.payoutType === 'FT' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(139, 92, 246, 0.1)',
      color: row.payoutType === 'FT' ? '#0EA5E9' : '#8B5CF6',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1174" data-mp="vu4udrwn8cvn" />,
    renderFilter: () => <FormControl size="small" fullWidth sx={{
      minWidth: 100
    }} data-id="element-1175" data-mp="hwgc8rtynjua">
          <Select multiple value={filters.payoutType} onChange={e => setFilters({
        ...filters,
        payoutType: e.target.value as PayoutType[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.join(', ')} data-id="element-1176" data-mp="3glowvr0064w4">
            <MenuItem value="FT" data-id="element-1177" data-mp="1opvy0tkjxqn4">FT</MenuItem>
            <MenuItem value="IBFT" data-id="element-1178" data-mp="1jr2mh6ots9pl">IBFT</MenuItem>
            <MenuItem value="OTC" data-id="element-1179" data-mp="1jsvscjrm6pwj">OTC</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'amountFCY',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap sx={{
      minWidth: 100
    }} data-id="element-1180" data-mp="1b028e91832cc">
          {row.currencyFCY} {row.amountFCY}
        </Typography>
  }, {
    id: 'currentState',
    label: 'Failed Stage',
    renderCell: row => <Chip label={row.currentState.replace(/_/g, ' ')} size="small" sx={{
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
      fontWeight: 600,
      fontSize: '0.7rem',
      minWidth: 120
    }} data-id="element-1181" data-mp="w2i8vhn3hzdk" />,
    renderFilter: () => <FormControl size="small" fullWidth sx={{
      minWidth: 120
    }} data-id="element-1182" data-mp="2fwkwexy507ry">
          <Select multiple value={filters.currentState} onChange={e => setFilters({
        ...filters,
        currentState: e.target.value as CurrentState[]
      })} displayEmpty sx={{
        height: 36,
        fontSize: '0.8rem'
      }} renderValue={selected => selected.length === 0 ? 'All' : selected.map(s => s.replace(/_/g, ' ')).join(', ')} data-id="element-1183" data-mp="21754vfqs7yb7">
            <MenuItem value="TITLE_FETCH" data-id="element-1184" data-mp="13lu6m5ohk642">TITLE FETCH</MenuItem>
            <MenuItem value="BALANCE_CHECK" data-id="element-1185" data-mp="13o6x578b7osq">BALANCE CHECK</MenuItem>
            <MenuItem value="FT_POST" data-id="element-1186" data-mp="zm3g832vfs02">FT POST</MenuItem>
            <MenuItem value="RAAST_POST" data-id="element-1187" data-mp="1dka02fazjrgq">RAAST POST</MenuItem>
            <MenuItem value="ONELINK_POST" data-id="element-1188" data-mp="1dlupxrmcum89">ONELINK POST</MenuItem>
            <MenuItem value="RTGS_POST" data-id="element-1189" data-mp="18kf4hyg9mdpc">RTGS POST</MenuItem>
          </Select>
        </FormControl>
  }, {
    id: 'attemptCount',
    label: 'Attempts',
    renderCell: row => <Chip label={`${row.attemptCount} attempt${row.attemptCount > 1 ? 's' : ''}`} size="small" sx={{
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      color: '#F59E0B',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1190" data-mp="1iil4gaiih9rk" />
  }, {
    id: 'lifecycleStatus',
    label: 'Lifecycle',
    renderCell: row => <Chip label="FAILED" size="small" sx={{
      backgroundColor: 'rgba(239, 68, 68, 0.1)',
      color: '#EF4444',
      fontWeight: 600,
      fontSize: '0.7rem'
    }} data-id="element-1191" data-mp="21bp4cdmboium" />
  }, {
    id: 'failedAt',
    label: 'Failed At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      minWidth: 150
    }} data-id="element-1192" data-mp="2irytodlbhmjf">
          {new Date(row.failedAt).toLocaleString()}
        </Typography>
  }];
  const isAllSelected = filteredTransactions.length > 0 && selectedIds.size === filteredTransactions.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < filteredTransactions.length;
  return <>
      <Box sx={{
      mb: 2,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 2
    }} data-id="element-1193" data-mp="1z24wtl3qwh8c">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }} data-id="element-1194" data-mp="x8v4n52zi708">
          <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} onChange={handleSelectAll} disabled={filteredTransactions.length === 0} data-id="element-1195" data-mp="11ztr6mekg1bx" />
          <Typography variant="body2" fontWeight={500} data-id="element-1196" data-mp="satk0fpwtzmn">
            {selectedIds.size} selected
          </Typography>
        </Box>
        <Button variant="contained" color="error" disabled={selectedIds.size === 0} startIcon={<Icon icon="lucide:x-circle" data-id="element-1198" data-mp="1616rkgyw3cbg" />} onClick={handleBulkCancelClick} sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: '8px',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
        }
      }} data-id="element-1197" data-mp="1fx9vnsdaxl11">
          Cancel Selected ({selectedIds.size})
        </Button>
      </Box>

      <DataTable title="Failed Transactions" data={filteredTransactions} columns={columns} rowActions={rowActions} keyField="id" filterChips={filterChips} onClearAllFilters={() => setFilterChips([])} emptyStateMessage="No failed transactions found" initialVisibleColumns={['selection', 'id', 'mtoName', 'payoutType', 'amountFCY', 'currentState', 'attemptCount', 'lifecycleStatus', 'failedAt']} onRowClick={row => {
      setSelectedTransaction(row);
      setDrawerOpen(true);
    }} data-id="element-1199" data-mp="2281ibbna905r" />

      {selectedTransaction && <TransactionDetailsDrawer open={drawerOpen} onClose={() => {
      setDrawerOpen(false);
      setSelectedTransaction(null);
    }} transaction={{
      ...selectedTransaction,
      currentState: selectedTransaction.currentState as any,
      lifecycleStatus: 'FAILED' as any
    } as any} data-id="element-1200" data-mp="14lb3l77klnpx" />}

      {selectedAmendTransaction && <AmendTransactionDialog open={amendDialogOpen} onClose={() => {
      setAmendDialogOpen(false);
      setSelectedAmendTransaction(null);
    }} onConfirm={handleAmendConfirm} transaction={selectedAmendTransaction} data-id="element-1201" data-mp="3g7mph4up8epv" />}

      {selectedCancelTransaction && <CancelTransactionDialog open={cancelDialogOpen} onClose={() => {
      setCancelDialogOpen(false);
      setSelectedCancelTransaction(null);
    }} onConfirm={handleCancelConfirm} transactionId={selectedCancelTransaction.id} cancellationAction={(selectedCancelTransaction as any).cancellationAction || 'SIMPLE_CANCEL'} data-id="element-1202" data-mp="2sik1zgddvrng" />}

      <CancelTransactionDialog open={bulkCancelDialogOpen} onClose={() => setBulkCancelDialogOpen(false)} onConfirm={handleBulkCancelConfirm} transactionId={`${selectedIds.size} selected transactions`} cancellationAction="BULK_CANCEL" data-id="element-1203" data-mp="fb2mmvfwpo3o" />

      {selectedTransaction && <RTGSActionDialog open={rtgsDialogOpen} onClose={() => {
      setRtgsDialogOpen(false);
      setSelectedTransaction(null);
    }} onConfirm={handleRTGSConfirm} transaction={selectedTransaction} action="send" data-id="element-1204" data-mp="k9m7esirktug" />}
    </>;
};
export default FailedTransactionsTable;